// src/components/LoginPage.js - Split-screen desktop-first layout
import { useState, useEffect } from "react";
import Icon from "./icons/Icon";
import { signInWithEmailAndPassword } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebase/config";
import { AuthBrandingPanel, AuthMobileHeader } from "./auth/AuthBrandingPanel";
import { AUTH_NOTICE_KEY } from "../hooks/useAuth";

// BL-087: our own branded reset email via the emailOutbox/ + Resend pipeline, replacing
// Firebase Auth's default mailer (firebaseapp.com sender, lands in Spam).
const sendUserPasswordResetFn = httpsCallable(functions, "sendUserPasswordReset");

export const LoginPage = () => {
  const [view, setView] = useState("LOGIN"); // LOGIN | FORGOT_PASSWORD | RESET_SENT
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resetEmail, setResetEmail] = useState("");

  // A sign-in can succeed and still be rejected afterwards by the auth handler
  // (a platform-console account has no tenant, BL-054). This component is remounted
  // by that flow, so the handler leaves its reason in sessionStorage rather than in
  // state. Read-and-remove, so the same message is never shown twice.
  useEffect(() => {
    let notice = null;
    try {
      notice = sessionStorage.getItem(AUTH_NOTICE_KEY);
      if (notice) sessionStorage.removeItem(AUTH_NOTICE_KEY);
    } catch (e) {
      // Site data blocked - there is simply no notice to show. This MUST NOT throw:
      // there is no error boundary above this route, so an uncaught error here
      // unmounts the whole app and white-screens /login for every visitor.
    }
    if (notice) setError(notice);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Email address is required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!password) {
      setError("Password is required.");
      return;
    }

    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, trimmedEmail, password);
      // useAuth hook will handle the redirection
    } catch (error) {
      setError(getLoginErrorMessage(error.code));
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Email address is required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      await sendUserPasswordResetFn({ email: trimmedEmail });
      setResetEmail(trimmedEmail);
      setView("RESET_SENT");
    } catch (error) {
      setError(getResetErrorMessage(error.code));
    } finally {
      setLoading(false);
    }
  };

  const switchToForgotPassword = () => {
    setError("");
    setPassword("");
    setView("FORGOT_PASSWORD");
  };

  const switchToLogin = () => {
    setError("");
    setView("LOGIN");
  };

  return (
    <div className="auth-container">
      {/* Left Branding Panel (Desktop Only) */}
      <AuthBrandingPanel />

      {/* Right Form Panel */}
      <div className="auth-form-panel">
        <div className="auth-form-container">
          <AuthMobileHeader />

          {/* ===== LOGIN VIEW ===== */}
          {view === "LOGIN" && (
            <>
              <div className="auth-form-header">
                <h2 className="auth-form-title">Welcome back</h2>
                <p className="auth-form-subtitle">Sign in to your account</p>
              </div>

              <form onSubmit={handleLogin} className="form" noValidate>
                {error && (
                  <div className="alert alert--error mb-6">
                    <div className="alert__content">{error}</div>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="email" className="form-label form-label--required">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="text"
                    inputMode="email"
                    autoCapitalize="none"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input"
                    placeholder="your-email@company.com"
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password" className="form-label form-label--required">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="form-input"
                    placeholder="Enter your password"
                  />
                </div>

                <div className="auth-forgot-link">
                  <button type="button" onClick={switchToForgotPassword}>
                    Forgot password?
                  </button>
                </div>

                <div className="form-group">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`btn btn--ph-primary btn--full btn--md ${
                      loading ? "btn--loading" : ""
                    }`}
                  >
                    {loading ? "Signing in..." : "Sign In"}
                  </button>
                </div>
              </form>
            </>
          )}

          {/* ===== FORGOT PASSWORD VIEW ===== */}
          {view === "FORGOT_PASSWORD" && (
            <>
              <div className="auth-form-header">
                <h2 className="auth-form-title">Reset your password</h2>
                <p className="auth-form-subtitle">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>

              <form onSubmit={handleForgotPassword} className="form" noValidate>
                {error && (
                  <div className="alert alert--error mb-6">
                    <div className="alert__content">{error}</div>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="reset-email" className="form-label form-label--required">
                    Email Address
                  </label>
                  <input
                    id="reset-email"
                    type="text"
                    inputMode="email"
                    autoCapitalize="none"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input"
                    placeholder="your-email@company.com"
                  />
                </div>

                <div className="form-group">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`btn btn--ph-primary btn--full btn--md ${
                      loading ? "btn--loading" : ""
                    }`}
                  >
                    {loading ? "Sending..." : "Send Reset Link"}
                  </button>
                </div>
              </form>

              <div className="auth-back-link">
                <button type="button" onClick={switchToLogin}>
                  <Icon name="arrow-left" />
                  Back to Login
                </button>
              </div>
            </>
          )}

          {/* ===== RESET EMAIL SENT VIEW ===== */}
          {view === "RESET_SENT" && (
            <>
              <div className="auth-form-header">
                <div className="auth-success-icon">
                  <Icon name="check" />
                </div>
                <h2 className="auth-form-title">Check your email</h2>
                <p className="auth-form-subtitle">
                  We've sent a password reset link to<br />
                  <strong>{resetEmail}</strong>
                </p>
              </div>

              <p className="auth-reset-note">
                Didn't receive the email? Check your spam folder or make sure the email address is correct.
              </p>

              <div className="auth-back-link">
                <button type="button" onClick={switchToLogin}>
                  <Icon name="arrow-left" />
                  Back to Login
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// The reset send is a CALLABLE now, so these are `functions/*` codes, not
// `auth/*`. The old auth/user-not-found and auth/too-many-requests branches are
// gone because the callable never returns them: it answers a byte-identical
// {ok:true} whether or not the address exists (anti-enumeration), so the only
// visible failure is a malformed address.
const getResetErrorMessage = (errorCode) => {
  switch (errorCode) {
    case "functions/invalid-argument":
      return "Please enter a valid email address.";
    default:
      return "An error occurred. Please try again.";
  }
};

const getLoginErrorMessage = (errorCode) => {
  switch (errorCode) {
    // 'auth/invalid-credential' is what modern Firebase returns for a bad
    // email/password: with Email Enumeration Protection on, the SDK collapses
    // wrong-password and user-not-found into it rather than revealing which
    // one failed (see signInWithEmailAndPassword in @firebase/auth's
    // auth-public.d.ts). Without this case a plain typo fell through to the
    // generic default below. The two legacy codes stay for projects with the
    // protection off.
    case "auth/invalid-credential":
    case "auth/user-not-found":
    case "auth/wrong-password":
      return "Invalid email or password. Please try again.";
    case "auth/too-many-requests":
      return "Too many failed attempts. Please try again later.";
    case "auth/user-disabled":
      return "This account has been disabled. Contact support.";
    default:
      return "An error occurred during sign in. Please try again.";
  }
};
